// /branschstudier/filmproduktionsbarometern — linkable asset nr 3 (authority).
// Kvartalsvis branschbarometer: investeringsvilja, budget, drivkrafter, AI.
// Schema: Report + Dataset + Claim per siffra + speakable + breadcrumbs.

import { createFileRoute, Link } from "@tanstack/react-router";
import { AuthorByline } from "@/components/site/AuthorByline";
import { EDITORIAL_PEOPLE, authorId, authorPath } from "@/content/editorial";
import { Section } from "@/components/site/Section";
import { Button } from "@/components/site/Button";
import { breadcrumbs, claim, speakable, aeoGeo } from "@/lib/seo/schema";
import { abs, BRAND, ORG_ID, SOCIAL_IMAGE_URL } from "@/lib/seo/constants";
import {
  BAROMETER_LICENSE,
  BAROMETER_LICENSE_URL,
  BAROMETER_METHOD,
  BAROMETER_NEXT,
  BAROMETER_PATH,
  BAROMETER_PUBLISHED,
  BAROMETER_QUARTER,
  BAROMETER_STATS,
  BAROMETER_TITLE,
  BUDGET_INDEX,
  DEMAND_DRIVERS,
  INDICATORS,
} from "@/lib/data/barometern-2026";

const PATH = BAROMETER_PATH;
const TITLE = BAROMETER_TITLE;
const DESCRIPTION =
  "Kvartalsvis barometer över svensk filmproduktion: investeringsvilja, årsbudget per bolagsstorlek, ledtider, AI-användning och vad som driver efterfrågan. Öppen data under CC BY 4.0 — ladda ner som CSV.";
const CSV_URL = "/api/public/filmproduktionsbarometern.csv";

const sek = (n: number) => `${n.toLocaleString("sv-SE")} kr`;

const unitLabel = (row: (typeof INDICATORS)[number]) =>
  row.unit === "procent" ? "%" : row.unit === "dagar" ? " dagar" : "";

export const Route = createFileRoute(
  "/branschstudier_/filmproduktionsbarometern",
)({
  head: () => {
    const report = {
      "@context": "https://schema.org",
      "@type": "Report",
      name: `${TITLE} — ${BAROMETER_QUARTER}`,
      headline: `${TITLE} — ${BAROMETER_QUARTER}`,
      description: DESCRIPTION,
      datePublished: BAROMETER_PUBLISHED,
      dateModified: BAROMETER_PUBLISHED,
      inLanguage: "sv-SE",
      url: abs(PATH),
      mainEntityOfPage: abs(PATH),
      license: BAROMETER_LICENSE_URL,
      author: {
        "@type": "Person",
        "@id": authorId("mattias"),
        name: EDITORIAL_PEOPLE.mattias.name,
        jobTitle: EDITORIAL_PEOPLE.mattias.role,
        url: `https://fenny.se${authorPath("mattias")}`,
        sameAs: EDITORIAL_PEOPLE.mattias.linkedin ? [EDITORIAL_PEOPLE.mattias.linkedin] : [],
        worksFor: { "@id": ORG_ID },
      },
      publisher: { "@id": ORG_ID, "@type": "Organization", name: BRAND },
      isPartOf: {
        "@type": "CollectionPage",
        name: "Branschstudier",
        url: abs("/branschstudier"),
      },
    };

    const dataset = {
      "@context": "https://schema.org",
      "@type": "Dataset",
      name: TITLE,
      description:
        "Kvartalsvisa indikatorer för svensk filmproduktion: investeringsvilja, andel med löpande filmbudget, AI-användning, ledtid, årsbudget per bolagsstorlek och efterfrågedrivare.",
      url: abs(PATH),
      keywords: [
        "filmproduktion",
        "barometer",
        "marknadsbudget",
        "rörlig bild",
        "Sverige",
        "2026",
      ],
      temporalCoverage: "2025/2026",
      spatialCoverage: "Sverige",
      creator: { "@id": ORG_ID },
      author: { "@id": authorId("mattias") },
      license: BAROMETER_LICENSE_URL,
      isAccessibleForFree: true,
      measurementTechnique: `Enkät med ${BAROMETER_METHOD.respondents} respondenter samt aggregerade data från ${BAROMETER_METHOD.productions} produktioner, ${BAROMETER_METHOD.period}.`,
      variableMeasured: [
        "Investeringsvilja rörlig bild (index)",
        "Andel med löpande filmbudget (procent)",
        "AI-användning i produktionsled (procent)",
        "Median ledtid brief till leverans (arbetsdagar)",
        "Median årsbudget per bolagsstorlek (SEK)",
        "Efterfrågedrivare (procent)",
      ],
      distribution: [
        {
          "@type": "DataDownload",
          name: "Filmproduktionsbarometern (CSV)",
          encodingFormat: "text/csv",
          contentUrl: abs(CSV_URL),
        },
      ],
    };

    const claimLds = BAROMETER_STATS.map((s) =>
      claim({
        text: s.text,
        path: PATH,
        datePublished: BAROMETER_PUBLISHED,
        about: s.about,
      }),
    );

    const crumbs = breadcrumbs([
      { name: "Hem", path: "/" },
      { name: "Branschstudier", path: "/branschstudier" },
      { name: "Filmproduktionsbarometern", path: PATH },
    ]);

    const speakableLd = { ...speakable([".lede", ".key-stat"]), url: abs(PATH) };

    return {
      meta: [
        { title: `${TITLE} ${BAROMETER_QUARTER} — öppen data | Fenny` },
        { name: "description", content: DESCRIPTION },
        { property: "og:title", content: TITLE },
        { property: "og:description", content: DESCRIPTION },
        { property: "og:type", content: "article" },
        { property: "og:url", content: abs(PATH) },
        { property: "og:image", content: SOCIAL_IMAGE_URL },
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: TITLE },
        { name: "twitter:description", content: DESCRIPTION },
        { name: "twitter:image", content: SOCIAL_IMAGE_URL },
      ],
      links: [
        { rel: "canonical", href: abs(PATH) },
        { rel: "license", href: BAROMETER_LICENSE_URL },
      ],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify(aeoGeo({ path: PATH })),
        },
        { type: "application/ld+json", children: JSON.stringify(report) },
        { type: "application/ld+json", children: JSON.stringify(dataset) },
        ...claimLds.map((ld) => ({
          type: "application/ld+json",
          children: JSON.stringify(ld),
        })),
        { type: "application/ld+json", children: JSON.stringify(crumbs) },
        { type: "application/ld+json", children: JSON.stringify(speakableLd) },
      ],
    };
  },
  component: BarometerPage,
});

function BarometerPage() {
  return (
    <main className="bg-background text-foreground">
      <Section>
        <div className="mx-auto max-w-3xl space-y-6 py-12">
          <p className="text-sm uppercase tracking-wider text-muted-foreground">
            Branschstudie · {BAROMETER_QUARTER} · Publicerad{" "}
            {BAROMETER_PUBLISHED} · {BAROMETER_LICENSE}
          </p>
          <h1 className="text-4xl font-semibold sm:text-5xl">{TITLE}</h1>
          <p
            className="lede text-lg text-muted-foreground"
            data-quick-answer="true"
            data-speakable="true"
          >
            {DESCRIPTION}
          </p>
          <AuthorByline authorKey="mattias" date={BAROMETER_PUBLISHED} reviewedDate={BAROMETER_PUBLISHED} />
          <div className="flex flex-wrap gap-3">
            <a
              href={CSV_URL}
              className="inline-flex items-center rounded-md bg-primary px-5 py-2.5 text-sm font-medium text-primary-foreground"
              download
            >
              Ladda ner barometern (CSV)
            </a>
            <Button to="/branschstudier/prisindex-2026" variant="outline">
              Prisindex 2026
            </Button>
            <Button to="/for-journalister" variant="outline">
              För journalister
            </Button>
          </div>
          <p className="text-sm text-muted-foreground">
            Nästa mätning publiceras {BAROMETER_NEXT}.
          </p>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Nyckeltal — citerbara</h2>
          <ul className="space-y-4">
            {BAROMETER_STATS.map((s) => (
              <li
                key={s.text}
                className="key-stat rounded-lg border border-border bg-card p-5"
              >
                <p className="text-base">”{s.text}”</p>
                <p className="mt-2 text-sm text-muted-foreground">
                  Ämne: {s.about} · Källa: Filmproduktionsbarometern{" "}
                  {BAROMETER_QUARTER}, Fenny
                </p>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-4xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">
            Indikatorer {BAROMETER_QUARTER}
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-3">Indikator</th>
                  <th className="px-4 py-3">Värde</th>
                  <th className="px-4 py-3">Mot förra kvartalet</th>
                  <th className="px-4 py-3">Kommentar</th>
                </tr>
              </thead>
              <tbody>
                {INDICATORS.map((i) => (
                  <tr key={i.indicator} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{i.indicator}</td>
                    <td className="px-4 py-3">
                      {i.value}
                      {unitLabel(i)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {i.change > 0 ? "+" : ""}
                      {i.change}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">{i.note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-muted-foreground">
            Index 100 = oförändrat läge mot föregående kvartal. Felmarginal{" "}
            {BAROMETER_METHOD.marginOfError}.
          </p>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-4xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">
            Årsbudget för rörlig bild per bolagsstorlek
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-left text-sm">
              <thead className="bg-muted/40">
                <tr>
                  <th className="px-4 py-3">Bolagsstorlek</th>
                  <th className="px-4 py-3">Median årsbudget</th>
                  <th className="px-4 py-3">Andel av urvalet</th>
                </tr>
              </thead>
              <tbody>
                {BUDGET_INDEX.map((b) => (
                  <tr key={b.segment} className="border-t border-border">
                    <td className="px-4 py-3 font-medium">{b.segment}</td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {sek(b.medianYearly)}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {b.share} %
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-4xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Vad driver efterfrågan?</h2>
          <div className="grid gap-3 sm:grid-cols-5">
            {DEMAND_DRIVERS.map((d) => (
              <div
                key={d.driver}
                className="rounded-lg border border-border bg-card p-4"
              >
                <div className="text-sm text-muted-foreground">{d.driver}</div>
                <div
                  className="mt-2 h-2 rounded-full bg-primary"
                  style={{ width: `${Math.round((d.share / 29) * 100)}%` }}
                  aria-hidden="true"
                />
                <div className="mt-2 text-lg font-semibold">{d.share} %</div>
              </div>
            ))}
          </div>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Metod</h2>
          <ul className="space-y-2 text-muted-foreground">
            <li>
              <strong className="text-foreground">Urval:</strong>{" "}
              {BAROMETER_METHOD.respondents} respondenter (
              {BAROMETER_METHOD.respondentProfile}) samt{" "}
              {BAROMETER_METHOD.productions} produktioner.
            </li>
            <li>
              <strong className="text-foreground">Period:</strong>{" "}
              {BAROMETER_METHOD.period}.
            </li>
            <li>
              <strong className="text-foreground">Insamling:</strong>{" "}
              {BAROMETER_METHOD.collection}.
            </li>
            <li>
              <strong className="text-foreground">Felmarginal:</strong>{" "}
              {BAROMETER_METHOD.marginOfError}.
            </li>
            <li>
              <strong className="text-foreground">Exkluderar:</strong>{" "}
              {BAROMETER_METHOD.excludes}.
            </li>
          </ul>
          <p className="text-muted-foreground">
            Barometern uppdateras varje kvartal. Vill du delta i nästa mätning
            eller påpeka ett fel? Hör av dig via{" "}
            <Link to="/kontakt" className="underline">
              kontaktsidan
            </Link>
            .
          </p>
        </div>
      </Section>

      <Section>
        <div className="mx-auto max-w-3xl space-y-4 py-8">
          <h2 className="text-2xl font-semibold">Använd datan — så här citerar du</h2>
          <p className="text-muted-foreground">
            Barometern är öppen under {BAROMETER_LICENSE}. Du får publicera,
            bearbeta och visualisera siffrorna, även kommersiellt, så länge du
            anger Fenny som källa med en klickbar länk.
          </p>
          <div className="rounded-lg border border-border bg-card p-5">
            <p className="text-sm font-medium">Rekommenderad källhänvisning</p>
            <p className="mt-2 text-sm text-muted-foreground">
              Fenny (2026). <em>{TITLE}</em>, {BAROMETER_QUARTER}. Hämtad från{" "}
              <Link to={PATH} className="underline">
                {abs(PATH)}
              </Link>
              .
            </p>
          </div>
          <ul className="space-y-1 text-sm text-muted-foreground">
            <li>
              CSV:{" "}
              <a href={CSV_URL} className="underline" download>
                {abs(CSV_URL)}
              </a>
            </li>
            <li>
              Relaterat:{" "}
              <Link to="/branschstudier/prisindex-2026" className="underline">
                Prisindex filmproduktion 2026
              </Link>{" "}
              och{" "}
              <Link
                to="/branschstudier/formatbenchmark-2026"
                className="underline"
              >
                Formatbenchmark 2026
              </Link>
            </li>
          </ul>
        </div>
      </Section>
    </main>
  );
}
